import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CanvasTransformService } from './canvas-transform.service';

export interface ScrollbarState {
	showScrollbars: boolean;
	thickness: number;
	horizontalWidth: number;
	verticalHeight: number;
	horizontalThumbWidth: number;
	verticalThumbHeight: number;
	horizontalThumbPosition: number;
	verticalThumbPosition: number;
	isHoveringHorizontal: boolean;
	isHoveringVertical: boolean;
	isDraggingHorizontal: boolean;
	isDraggingVertical: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class ScrollbarService {
	private readonly normalThickness = 4;
	private readonly hoverThickness = 8;
	private updateInProgress = false; // Protection contre les boucles infinies

	private state: ScrollbarState = {
		showScrollbars: true,
		thickness: this.normalThickness,
		horizontalWidth: 0,
		verticalHeight: 0,
		horizontalThumbWidth: 0,
		verticalThumbHeight: 0,
		horizontalThumbPosition: 0,
		verticalThumbPosition: 0,
		isHoveringHorizontal: false,
		isHoveringVertical: false,
		isDraggingHorizontal: false,
		isDraggingVertical: false
	};

	private horizontalThumbOffset = 0;
	private verticalThumbOffset = 0;
	private initialMouseX = 0;
	private initialMouseY = 0;

	private stateSubject = new BehaviorSubject<ScrollbarState>(this.state);
	public state$ = this.stateSubject.asObservable();

	private eventListenersSetup = false;

	constructor(private canvasTransformService: CanvasTransformService) {}

	updateScrollbars(): void {
		if (this.updateInProgress) return; // Éviter les boucles infinies

		this.updateInProgress = true;

		try {
			const scrollbarData = this.canvasTransformService.getScrollbarData();
			const dimensions = this.canvasTransformService.getDimensions();

			if (scrollbarData.horizontal.needed) {
				this.state.horizontalWidth = dimensions.containerWidth - this.normalThickness - 5;
				this.state.horizontalThumbWidth = Math.max(
					30,
					scrollbarData.horizontal.thumbSize * this.state.horizontalWidth
				);
				this.state.horizontalThumbPosition =
					scrollbarData.horizontal.scrollPercent * (this.state.horizontalWidth - this.state.horizontalThumbWidth);
			}

			if (scrollbarData.vertical.needed) {
				this.state.verticalHeight = dimensions.containerHeight - this.normalThickness - 5;
				this.state.verticalThumbHeight = Math.max(
					30,
					scrollbarData.vertical.thumbSize * this.state.verticalHeight
				);
				this.state.verticalThumbPosition =
					scrollbarData.vertical.scrollPercent * (this.state.verticalHeight - this.state.verticalThumbHeight);
			}

			this.emitState();
		} finally {
			this.updateInProgress = false;
		}
	}

	setupEventListeners(containerElement: HTMLElement): void {
		if (this.eventListenersSetup) {
			this.removeEventListeners();
		}

		setTimeout(() => {
			const horizontalThumb = containerElement.querySelector('.horizontal-thumb') as HTMLElement;
			const verticalThumb = containerElement.querySelector('.vertical-thumb') as HTMLElement;

			if (horizontalThumb) {
				horizontalThumb.addEventListener('mousedown', this.handleHorizontalThumbMouseDown);
				horizontalThumb.addEventListener('click', (e) => e.stopPropagation());
			}

			if (verticalThumb) {
				verticalThumb.addEventListener('mousedown', this.handleVerticalThumbMouseDown);
				verticalThumb.addEventListener('click', (e) => e.stopPropagation());
			}

			window.addEventListener('mousemove', this.handleScrollbarMouseMove);
			window.addEventListener('mouseup', this.handleScrollbarMouseUp);

			this.eventListenersSetup = true;
		}, 100);
	}

	removeEventListeners(): void {
		if (!this.eventListenersSetup) return;

		window.removeEventListener('mousemove', this.handleScrollbarMouseMove);
		window.removeEventListener('mouseup', this.handleScrollbarMouseUp);

		this.eventListenersSetup = false;
	}

	private handleHorizontalThumbMouseDown = (e: MouseEvent): void => {
		e.preventDefault();
		e.stopPropagation();

		const dimensions = this.canvasTransformService.getDimensions();
		this.horizontalThumbOffset = e.clientX - this.state.horizontalThumbPosition;
		this.initialMouseX = e.clientX;
		this.state.isDraggingHorizontal = true;
		this.emitState();
	};

	private handleVerticalThumbMouseDown = (e: MouseEvent): void => {
		e.preventDefault();
		e.stopPropagation();

		const dimensions = this.canvasTransformService.getDimensions();
		this.verticalThumbOffset = e.clientY - this.state.verticalThumbPosition;
		this.initialMouseY = e.clientY;
		this.state.isDraggingVertical = true;
		this.emitState();
	};

	private handleScrollbarMouseMove = (e: MouseEvent): void => {
		this.updateHoverState(e);

		if (this.state.isDraggingHorizontal) {
			this.handleHorizontalDrag(e);
		}

		if (this.state.isDraggingVertical) {
			this.handleVerticalDrag(e);
		}
	};

	private updateHoverState(e: MouseEvent): void {
		const dimensions = this.canvasTransformService.getDimensions();
		const scrollbarData = this.canvasTransformService.getScrollbarData();

		// Calculer les zones de hover pour les scrollbars
		const horizontalScrollbarRect = {
			left: 0,
			top: dimensions.containerHeight - this.state.thickness - 3,
			right: this.state.horizontalWidth,
			bottom: dimensions.containerHeight - 3
		};

		const verticalScrollbarRect = {
			left: dimensions.containerWidth - this.state.thickness - 3,
			top: 0,
			right: dimensions.containerWidth - 3,
			bottom: this.state.verticalHeight
		};

		const isHoveringHorizontal = scrollbarData.horizontal.needed &&
			e.clientX >= horizontalScrollbarRect.left &&
			e.clientX <= horizontalScrollbarRect.right &&
			e.clientY >= horizontalScrollbarRect.top &&
			e.clientY <= horizontalScrollbarRect.bottom;

		const isHoveringVertical = scrollbarData.vertical.needed &&
			e.clientX >= verticalScrollbarRect.left &&
			e.clientX <= verticalScrollbarRect.right &&
			e.clientY >= verticalScrollbarRect.top &&
			e.clientY <= verticalScrollbarRect.bottom;

		if (isHoveringHorizontal !== this.state.isHoveringHorizontal ||
			isHoveringVertical !== this.state.isHoveringVertical) {

			this.state.isHoveringHorizontal = isHoveringHorizontal;
			this.state.isHoveringVertical = isHoveringVertical;
			this.updateScrollbarSize();
		}
	}

	private handleHorizontalDrag(e: MouseEvent): void {
		const maxThumbPosition = this.state.horizontalWidth - this.state.horizontalThumbWidth;
		const newThumbPosition = Math.max(0, Math.min(maxThumbPosition, e.clientX - this.horizontalThumbOffset));

		const scrollPercent = newThumbPosition / maxThumbPosition;
		const dimensions = this.canvasTransformService.getDimensions();
		const transform = this.canvasTransformService.getTransform();
		const scaledCanvasWidth = dimensions.canvasWidth * transform.scale;
		const maxTranslateX = scaledCanvasWidth - dimensions.containerWidth;

		const newTranslateX = -scrollPercent * maxTranslateX;
		this.canvasTransformService.applyTranslationLimits(newTranslateX, transform.translateY);
	}

	private handleVerticalDrag(e: MouseEvent): void {
		const maxThumbPosition = this.state.verticalHeight - this.state.verticalThumbHeight;
		const newThumbPosition = Math.max(0, Math.min(maxThumbPosition, e.clientY - this.verticalThumbOffset));

		const scrollPercent = newThumbPosition / maxThumbPosition;
		const dimensions = this.canvasTransformService.getDimensions();
		const transform = this.canvasTransformService.getTransform();
		const scaledCanvasHeight = dimensions.canvasHeight * transform.scale;
		const maxTranslateY = scaledCanvasHeight - dimensions.containerHeight;

		const newTranslateY = -scrollPercent * maxTranslateY;
		this.canvasTransformService.applyTranslationLimits(transform.translateX, newTranslateY);
	}

	private updateScrollbarSize(): void {
		const currentThickness = (this.state.isHoveringHorizontal || this.state.isHoveringVertical ||
			this.state.isDraggingHorizontal || this.state.isDraggingVertical)
			? this.hoverThickness
			: this.normalThickness;

		if (this.state.thickness !== currentThickness) {
			this.state.thickness = currentThickness;
			this.emitState();
		}
	}

	private handleScrollbarMouseUp = (): void => {
		this.state.isDraggingHorizontal = false;
		this.state.isDraggingVertical = false;
		this.horizontalThumbOffset = 0;
		this.verticalThumbOffset = 0;
		this.initialMouseX = 0;
		this.initialMouseY = 0;
		this.emitState();
	};

	shouldShowHorizontalScrollbar(dataLength: number): boolean {
		if (dataLength < 1) return false;
		return this.state.showScrollbars && this.canvasTransformService.needsHorizontalScrollbar();
	}

	shouldShowVerticalScrollbar(dataLength: number): boolean {
		if (dataLength < 1) return false;
		return this.state.showScrollbars && this.canvasTransformService.needsVerticalScrollbar();
	}

	getHorizontalScrollbarStyles(): any {
		const currentThickness = (this.state.isHoveringHorizontal || this.state.isDraggingHorizontal)
			? this.hoverThickness
			: this.normalThickness;

		return {
			width: `${this.state.horizontalWidth}px`,
			height: `${currentThickness}px`,
			bottom: '3px',
			left: '0px',
		};
	}

	getVerticalScrollbarStyles(): any {
		const currentThickness = (this.state.isHoveringVertical || this.state.isDraggingVertical)
			? this.hoverThickness
			: this.normalThickness;

		return {
			width: `${currentThickness}px`,
			height: `${this.state.verticalHeight}px`,
			top: '0px',
			right: '3px',
		};
	}

	getHorizontalThumbStyles(): any {
		const currentThickness = (this.state.isHoveringHorizontal || this.state.isDraggingHorizontal)
			? this.hoverThickness
			: this.normalThickness;

		return {
			width: `${this.state.horizontalThumbWidth}px`,
			height: `${currentThickness}px`,
			left: `${this.state.horizontalThumbPosition}px`,
		};
	}

	getVerticalThumbStyles(): any {
		const currentThickness = (this.state.isHoveringVertical || this.state.isDraggingVertical)
			? this.hoverThickness
			: this.normalThickness;

		return {
			width: `${currentThickness}px`,
			height: `${this.state.verticalThumbHeight}px`,
			top: `${this.state.verticalThumbPosition}px`,
		};
	}

	getState(): ScrollbarState {
		return { ...this.state };
	}

	private emitState(): void {
		// Éviter les émissions inutiles
		const currentState = this.stateSubject.value;
		if (JSON.stringify(currentState) !== JSON.stringify(this.state)) {
			this.stateSubject.next({ ...this.state });
		}
	}

	reset(): void {
		this.state = {
			showScrollbars: true,
			thickness: this.normalThickness,
			horizontalWidth: 0,
			verticalHeight: 0,
			horizontalThumbWidth: 0,
			verticalThumbHeight: 0,
			horizontalThumbPosition: 0,
			verticalThumbPosition: 0,
			isHoveringHorizontal: false,
			isHoveringVertical: false,
			isDraggingHorizontal: false,
			isDraggingVertical: false
		};
		this.emitState();
	}
}
